import React, { useState } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";

const TodaysSummary = ({ companyOptions, entries, onEdit, onDelete }) => {
  const [expandedEntry, setExpandedEntry] = useState(null);

  const calculateTotals = () => {
    return entries.reduce(
      (totals, entry) => ({
        totalSales: totals.totalSales + entry.sales_amount,
        totalCollections: totals.totalCollections + entry.collection_amount,
        totalEntries: totals.totalEntries + 1,
      }),
      { totalSales: 0, totalCollections: 0, totalEntries: 0 }
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "INR",
    }).format(amount);
  };

  const getCollectionRateColor = (rate) => {
    if (rate >= 90) return "text-success";
    if (rate >= 70) return "text-warning";
    return "text-error";
  };

  const getCompanyName = (companyValue) => {
    const company = companyOptions.find((c) => c.value === companyValue);

    return company ? company.label : companyValue;
  };

  const totals = calculateTotals();
  const overallCollectionRate =
    totals.totalSales > 0
      ? (totals.totalCollections / totals.totalSales) * 100
      : 0;

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">
            Today's Summary
          </h3>
          <p className="text-sm text-muted-foreground">
            {new Date().toLocaleDateString("en-US", {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
            <span className="text-sm font-medium text-primary">
              {totals.totalEntries}
            </span>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-4 mb-6">
        <div className="bg-muted/50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Total Sales</p>
              <p className="text-xl font-semibold text-foreground">
                {formatCurrency(totals.totalSales)}
              </p>
            </div>
            <Icon name="TrendingUp" size={20} className="text-primary" />
          </div>
        </div>

        <div className="bg-muted/50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Total Collections</p>
              <p className="text-xl font-semibold text-foreground">
                {formatCurrency(totals.totalCollections)}
              </p>
            </div>
            <Icon name="BadgeIndianRupee" size={20} className="text-success" />
          </div>
        </div>

        <div className="bg-muted/50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Collection Rate</p>
              <p
                className={`text-xl font-semibold ${getCollectionRateColor(
                  overallCollectionRate
                )}`}
              >
                {overallCollectionRate.toFixed(1)}%
              </p>
            </div>
            <Icon
              name="Target"
              size={20}
              className={getCollectionRateColor(overallCollectionRate)}
            />
          </div>
        </div>
      </div>

      {/* Entries List */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-foreground mb-3">
          Recent Entries
        </h4>

        {entries.length === 0 ? (
          <div className="text-center py-8">
            <Icon
              name="FileText"
              size={32}
              className="text-muted-foreground mx-auto mb-2"
            />
            <p className="text-sm text-muted-foreground">
              No entries for today
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Start by adding your first entry
            </p>
          </div>
        ) : (
          entries.map((entry) => {
            const collectionRate =
              entry.sales_amount > 0
                ? (entry.collection_amount / entry.sales_amount) * 100
                : 0;
            const isExpanded = expandedEntry === entry.id;

            return (
              <div
                key={entry.id}
                className="border border-border rounded-lg overflow-hidden"
              >
                <div
                  className="p-4 cursor-pointer hover:bg-muted/50 transition-colors duration-150"
                  onClick={() => setExpandedEntry(isExpanded ? null : entry.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium text-foreground">
                          {getCompanyName(entry.company_id)}
                        </span>
                        <span
                          className={`text-xs px-2 py-1 rounded-full ${getCollectionRateColor(
                            entry.collection_rate
                          )} bg-current/10`}
                        >
                          {entry.collection_rate.toFixed(1)}%
                        </span>
                      </div>
                      <div className="flex items-center space-x-4 mt-1">
                        <span className="text-xs text-muted-foreground">
                          Sales: {formatCurrency(entry.sales_amount)}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          Collection: {formatCurrency(entry.collection_amount)}
                        </span>
                      </div>
                    </div>
                    <Icon
                      name={isExpanded ? "ChevronUp" : "ChevronDown"}
                      size={16}
                      className="text-muted-foreground"
                    />
                  </div>
                </div>

                {isExpanded && (
                  <div className="px-4 pb-4 border-t border-border bg-muted/25">
                    <div className="pt-3 space-y-2">
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">Date:</span>
                        <span className="text-foreground">
                          {new Date(entry.entry_date).toLocaleDateString()}
                        </span>
                      </div>
                      {entry.notes && (
                        <div className="flex justify-between text-xs">
                          <span className="text-muted-foreground">Notes:</span>
                          <span className="text-foreground text-right max-w-32 truncate">
                            {entry.notes}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">
                          Submitted:
                        </span>
                        <span className="text-foreground">
                          {new Date(entry.updated_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center justify-end space-x-2 mt-3 pt-3 border-t border-border">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onEdit(entry)}
                        iconName="Edit2"
                        iconPosition="left"
                      >
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onDelete(entry.id)}
                        iconName="Trash2"
                        iconPosition="left"
                        className="text-error hover:text-error"
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default TodaysSummary;
